import { RenderHTML } from '@wix/cc-user-facing-common-components';
import { Notification } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useState } from 'react';

import {
  AI_TRANSLATED_ARTICLE_KEY_BY_LOCALE,
  AI_TRANSLATED_ARTICLE_KEY_DEFAULT,
  AI_TRANSLATED_HOMEPAGE_KEY_BY_LOCALE,
  AI_TRANSLATED_HOMEPAGE_KEY_DEFAULT,
  AI_TRANSLATED_LABEL_ID,
  FULLY_AI_TRANSLATED_LOCALES,
} from './consts';
import { LOCALES } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { PageType } from '../../../types';
import { useStore } from '../../../zustand/stores';

type ArticleLabel = { id?: string };

type AiTranslatedNotificationProps = {
  pageType: PageType;
};

const hasAiTranslatedLabel = (labels?: ArticleLabel[]): boolean =>
  !!labels?.some((label) => label.id === AI_TRANSLATED_LABEL_ID);

// The translation key of the banner to render, or null when there is none.
const getAiTranslatedKey = (
  locale: string | undefined,
  pageType: PageType,
  articleLabels?: ArticleLabel[]
): string | null => {
  const isHomepage = pageType === PageType.Homepage;
  const isArticle = pageType === PageType.Article;

  if (!isHomepage && !isArticle) {
    return null;
  }

  if (!locale || locale === LOCALES.EN) {
    return null;
  }

  const isFullyAiTranslated = FULLY_AI_TRANSLATED_LOCALES.includes(locale);

  if (isHomepage) {
    if (!isFullyAiTranslated) {
      return null;
    }
    return (
      AI_TRANSLATED_HOMEPAGE_KEY_BY_LOCALE[locale] ??
      AI_TRANSLATED_HOMEPAGE_KEY_DEFAULT
    );
  }

  // A hybrid Help Center banners only the articles that carry the label; a fully
  // AI-translated one banners every article, labelled or not.
  if (!isFullyAiTranslated && !hasAiTranslatedLabel(articleLabels)) {
    return null;
  }

  return (
    AI_TRANSLATED_ARTICLE_KEY_BY_LOCALE[locale] ??
    AI_TRANSLATED_ARTICLE_KEY_DEFAULT
  );
};

export const AiTranslatedNotification: FunctionComponent<AiTranslatedNotificationProps> =
  ({ pageType }) => {
    const { t } = useTranslation();
    const { locale } = useRouter();
    const articleLabels = useStore((state) => state.articleSlice.article?.labels);

    const notificationKey = getAiTranslatedKey(locale, pageType, articleLabels);

    const [isDisplay, setIsDisplay] = useState(true);

    if (!notificationKey) {
      return null;
    }

    // Named in the reader's own language: "italiano", not "Italian".
    const language = locale
      ? new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale
      : undefined;

    return (
      <Notification
        dataHook={DATA_HOOKS.AI_TRANSLATED_NOTIFICATION}
        show={isDisplay}
        theme="standard"
        onClose={() => setIsDisplay(false)}
      >
        <Notification.TextLabel ellipsis={false} textAlign="center">
          <RenderHTML html={t(notificationKey, { language })} />
        </Notification.TextLabel>
        <Notification.CloseButton />
      </Notification>
    );
  };
